import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { omit as _omit } from 'lodash';

import EntityStateUpdater from 'component/EntityStateUpdater';

import { formStatusDispatchHelper } from 'actions/FormStatusActions';
import { blockForm, releaseForm } from 'actions/ActiveFormsActions';
import { addNotification } from 'actions/NotificationsActions';

import Breadcrumb from 'components/layout/Breadcrumb';
import Tabs from 'components/layout/Tabs';

import WhiteLabelData from 'components/layout/whiteLabel/WhiteLabelData';
import WhiteLabelLogoAndFav from 'components/layout/whiteLabel/WhiteLabelLogoAndFav';
import WhiteLabelStyle from 'components/layout/whiteLabel/WhiteLabelStyle';
import WhiteLabelTheme from 'components/layout/whiteLabel/WhiteLabelTheme';
import WhiteLabelPages from 'components/layout/whiteLabel/WhiteLabelPages';
import WhiteLabelMainMenu from 'components/layout/whiteLabel/WhiteLabelMainMenu';
import WhiteLabelPaymentConfig from 'components/layout/whiteLabel/WhiteLabelPaymentConfig';
import WhiteLabelSystemMails from 'components/layout/whiteLabel/WhiteLabelSystemMails';
import WhiteLabelSalesDocuments from 'components/layout/whiteLabel/WhiteLabelSalesDocuments';
import WhiteLabelPlugins from 'components/layout/whiteLabel/WhiteLabelPlugins';
import WhiteLabelClientRegistration from 'components/layout/whiteLabel/WhiteLabelClientRegistration';
import WhiteLabelFinancialSettings from 'components/layout/whiteLabel/WhiteLabelFinancialSettings';

import Entity from 'components/Entity';
import Data from 'components/Data';

import { entityMetaMapping, ROLE_ROOT_EMPLOYEE } from 'js/constants';
import may from 'js/security';

import ActiveFormsToolbar from 'components/forms/ActiveFormsToolbar';
import FormErrors from 'components/forms/FormErrors';
import ProductTemplateCSS from '../productTemplate/ProductTemplateCSS';

class WhiteLabelView extends EntityStateUpdater {
  constructor(props) {
    super(props);

    this.state = {
      entityLoaded: false,
      entityChanged: false,
      activeFormsInitiated: false,
    };

    this.entity = new Entity();

    if (props.meta[entityMetaMapping.whiteLabel] !== undefined && !props.meta[entityMetaMapping.whiteLabel].pending) {
      this.entity.setMeta(props.meta, entityMetaMapping.whiteLabel);
    }

    if (this.entity.getDataLoaded() && this.entity.getMetaLoaded()) {
      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }

    this.dataRef = React.createRef();
  }

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      meta,
      whiteLabelId,
      data,
      formStatus,
      activeForms,
      doNotify,
      doBlockForm,
    } = this.props;
    const { activeFormsInitiated, entity } = this.state;

    if (!prevProps.activeForms.allowEdit && activeForms.allowEdit) {
      if (this.dataRef && this.dataRef.current) {
        this.dataRef.current.fetch();
      }
      this.setState({ entityLoaded: false });
    }

    // white label data was saved and there was no error
    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError) {
      this.setState({ entityChanged: false });
      doNotify(1, messages.white_label_saved);
    }

    if (prevProps.formStatus.pending && !formStatus.pending && formStatus.hasError) {
      this.setState({ entityChanged: false });
      doNotify(0, formStatus.errors.message);
    }

    if (prevProps.data.pending && !data.pending) {
      this.entity.setData(data.data);
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }

      // we received the white label settings data
      // now we have to see if we can block the page edit to ourselves
      if (!activeFormsInitiated && !may('ROLE_ROOT_EMPLOYEE', this.props.user.roles)) {
        this.setState({ activeFormsInitiated: true }, () => doBlockForm(whiteLabelId));
      }
    }

    if (
      meta[entityMetaMapping.whiteLabel] !== undefined &&
      !meta[entityMetaMapping.whiteLabel].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.whiteLabel);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (entity && entity.entityLoaded && (!formStatus.pending && formStatus.response && formStatus.response.plugins)) {
      if (entity.data.plugins.length !== formStatus.response.plugins.length) {
        const plugins = [];
        for (let i = 0; i < formStatus.response.plugins.length; i++) {
          plugins.push(entity.createValue('plugins', [], formStatus.response.plugins[i]));
        }
        this.updateEntity('plugins', plugins, []);
      }
    }
  };

  componentWillUnmount = () => {
    const { whiteLabelId, doReleaseForm } = this.props;
    // release the block so that other users may work on the white label settings
    doReleaseForm(whiteLabelId);
  };

  formSubmit = e => {
    e.preventDefault();

    const { whiteLabelId, activeForms, onSubmit } = this.props;

    if (activeForms.allowEdit) {
      this.setState(
        prevState => ({ entity: prevState.entity.validate() }),
        () => {
          if (this.state.entity.isValid) {
            const data = _omit(this.state.entity.processedData, [
              'mainLogo',
              'scrollLogo',
              'mainLogoSvg',
              'scrollLogoSvg',
              'mailLogo',
              'favicon',
              'faviconIphone',
              'faviconIphoneRetina',
              'faviconIpad',
              'faviconIpadRetina',
              'faviconAndroid',
              'faviconMicrosoft',
            ]);
            onSubmit(whiteLabelId, data);
          }
        }
      );
    }
  };

  getTabHeaderList = () => {
    const {
      intl: { messages },
      user,
    } = this.props;
    const result = [];

    result.push(messages.white_label);
    result.push(messages.logos);
    result.push(messages.style);
    result.push(messages.theme);
    result.push(messages.main_menu);
    result.push(messages.cms_pages);
    result.push(messages.emails);
    result.push(messages.payment_config);
    result.push(messages.sales_documents);
    result.push(messages.plugins);
    result.push(messages.signup);
    if (may(ROLE_ROOT_EMPLOYEE, user.roles)) {
      result.push(messages.financial);
      result.push(messages.template_styles);
    }

    return result;
  };

  getTabContentList = () => {
    const { formStatus, activeForms, user, intl: { messages } } = this.props;
    const { entity, entityLoaded } = this.state;
    const result = [];

    const disabled = formStatus.pending || !activeForms.allowEdit;
    const sharedProps = { entityLoaded, entity, changeEntity: this.changeEntity, disabled, messages };

    result.push(<WhiteLabelData {...sharedProps} />);
    result.push(<WhiteLabelLogoAndFav {...sharedProps} />);
    result.push(<WhiteLabelStyle {...sharedProps} />);
    result.push(<WhiteLabelTheme {...sharedProps} />);
    result.push(<WhiteLabelMainMenu {...sharedProps} />);
    result.push(<WhiteLabelPages {...sharedProps} />);
    result.push(<WhiteLabelSystemMails {...sharedProps} />);
    result.push(<WhiteLabelPaymentConfig {...sharedProps} />);
    result.push(<WhiteLabelSalesDocuments {...sharedProps} />);
    result.push(<WhiteLabelPlugins {...sharedProps} />);
    result.push(<WhiteLabelClientRegistration {...sharedProps} />);
    if (may(ROLE_ROOT_EMPLOYEE, user.roles)) {
      result.push(<WhiteLabelFinancialSettings {...sharedProps} />);
      result.push(<ProductTemplateCSS {...sharedProps} />);
    }

    return result;
  };

  getTabKeepMounted = () => {
    const keepMounted = [];
    keepMounted.push(0);
    keepMounted.push(5);

    return keepMounted;
  };

  getBreadcrumb = () => {
    const { breadcrumb, crumbs } = this.props;
    const { entityLoaded, entity } = this.state;

    if (breadcrumb) {
      return breadcrumb;
    }

    const breadcrumbs = crumbs.slice();
    breadcrumbs.push({ link: null, name: entityLoaded ? entity.getName() : null });

    return <Breadcrumb crumbs={breadcrumbs} />;
  };

  render() {
    const { formStatus, data, whiteLabelId } = this.props;
    const { entityChanged } = this.state;

    return (
      <div className="padding-25 sm-padding-10">
        <Data ref={this.dataRef} dataName="whiteLabel" url={`/api/whitelabels/${whiteLabelId}.json`} data={data} />
        <FormErrors formStatus={formStatus} />

        <form method="put" onSubmit={this.formSubmit}>
          {this.getBreadcrumb()}

          <Row>
            <Col xs={12}>
              <ActiveFormsToolbar
                formStatus={formStatus}
                entityChanged={entityChanged}
                formType="whiteLabelSettings"
                formId={whiteLabelId}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Tabs
                tabs={this.getTabHeaderList()}
                tabContent={this.getTabContentList()}
                keepMounted={this.getTabKeepMounted()}
              />
            </Col>
          </Row>
        </form>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    data: state.data.whiteLabel,
    meta: state.entityMeta,
    formStatus: state.formStatus.whiteLabelEdit,
    activeForms: state.activeForms,
    user: state.login.user,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    onSubmit: (whiteLabelId, args) => {
      formStatusDispatchHelper('whiteLabelEdit', `/api/whitelabels/${whiteLabelId}.json`, args, 'put', dispatch);
    },
    doBlockForm: id => {
      dispatch(blockForm('whiteLabelSettings', id));
    },
    doReleaseForm: id => {
      dispatch(releaseForm('whiteLabelSettings', id));
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(WhiteLabelView)
  )
);
